import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import FullBlockingModal from '../../common/FullBlockingModal';
import { Button } from '../../common/Button';
import { ServiceCategory } from '../../../types/service';
import { useProfileStore } from '../../../stores/profileStore';
import { useAdminStore } from '../../../stores/adminStore';
import { useServiceCategoryStore } from '../../../stores/serviceCategoryStore';
import GenericInformationModal from '../../common/GenericInformationModal';
import { getDisplayErrorMessage } from '../../../utils/errors';

interface Props {
  serviceCategory?: ServiceCategory | null;
  onClose: () => void;
}

const getServiceCategoryDescription = (serviceCategory?: ServiceCategory | null) =>
  serviceCategory?.description ?? serviceCategory?.service_description ?? '';

const getInitialLabelFr = (serviceCategory?: ServiceCategory | null) =>
  serviceCategory?.label_fr?.trim() || serviceCategory?.label?.trim() || '';

const getInitialLabelEn = (serviceCategory?: ServiceCategory | null) =>
  serviceCategory?.label_en?.trim() || '';

const AddServiceCategoryModal: React.FC<Props> = ({ serviceCategory = null, onClose }) => {
  const { t } = useTranslation(['common', 'admin']);
  const isEditing = Boolean(serviceCategory?.id);
  const [labelFr, setLabelFr] = useState(() => getInitialLabelFr(serviceCategory));
  const [labelEn, setLabelEn] = useState(() => getInitialLabelEn(serviceCategory));
  const [description, setDescription] = useState(getServiceCategoryDescription(serviceCategory));
  const [saving, setSaving] = useState(false);
  const [saveErrorMessage, setSaveErrorMessage] = useState<string | null>(null);

  const { suggestNewService } = useProfileStore();
  const { updateServiceCategory } = useAdminStore();
  const { upsertServiceCategory } = useServiceCategoryStore();

  const handleSubmit = async () => {
    setSaving(true);
    setSaveErrorMessage(null);

    try {
      if (isEditing && serviceCategory) {
        const updatedCategory = await updateServiceCategory(serviceCategory.id, {
          labelFr: labelFr.trim(),
          labelEn: labelEn.trim() || null,
          description,
        });
        upsertServiceCategory(updatedCategory);
      } else {
        const newCategory = await suggestNewService({
          label: labelFr.trim(),
          description,
        });
        upsertServiceCategory(newCategory);
      }

      onClose();
    } catch (err) {
      console.error('Failed to save service category', err);
      setSaveErrorMessage(
        getDisplayErrorMessage(
          err,
          t('serviceCategorySaveErrorFallback', {
            ns: 'admin',
            defaultValue: 'An unexpected error occurred while saving the service category.',
          })
        )
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <FullBlockingModal innerClassName="md:max-w-[36rem]">
        <h2 className="text-2xl font-bold text-secondary mb-4">
          {isEditing
            ? t('edit_service_category', { ns: 'admin' })
            : t('add_new_service', { ns: 'common' })}
        </h2>

        <div className="flex flex-col gap-3">
          <label className="text-sm font-semibold text-gray-700">
            {t('table_fields.label_fr', { ns: 'admin' })}
          </label>
          <input
            value={labelFr}
            onChange={e => setLabelFr(e.target.value)}
            className="border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
          />

          <label className="text-sm font-semibold text-gray-700">
            {t('table_fields.label_en', { ns: 'admin' })}
          </label>
          <input
            value={labelEn}
            onChange={e => setLabelEn(e.target.value)}
            className="border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
          />
          <p className="text-xs text-gray-500">
            {t('service_category_label_hint', { ns: 'admin' })}
          </p>

          <label className="text-sm font-semibold text-gray-700">
            {t('table_fields.description', { ns: 'admin' })}
          </label>
          <textarea
            value={description}
            onChange={e => setDescription(e.target.value)}
            className="border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-primary h-24"
          />
        </div>

        <div className="flex flex-row md:flex-row justify-end gap-4 mt-6">
          <Button
            variant="simple-text"
            onClick={onClose}
            className="font-bold rounded-full px-8 py-2 w-full md:w-auto"
            type="button"
          >
            {t('common:form_actions.cancel', { ns: 'common' })}
          </Button>

          <Button
            variant="primary"
            onClick={handleSubmit}
            className="font-bold rounded-full px-8 py-2 w-full md:w-auto"
            type="button"
            disabled={saving || !labelFr.trim()}
          >
            {saving
              ? t('loading', { ns: 'common' })
              : t('common:form_actions.save', { ns: 'common' })}
          </Button>
        </div>
      </FullBlockingModal>

      {saveErrorMessage && (
        <GenericInformationModal
          title={
            isEditing
              ? t('serviceCategoryUpdateError', { ns: 'admin' })
              : t('serviceCategoryCreateError', {
                  ns: 'admin',
                  defaultValue: 'Failed to create service category',
                })
          }
          message=""
          onClose={() => setSaveErrorMessage(null)}
          errorDetails={saveErrorMessage}
          closeButtonLabel={t('common:form_actions.close', { ns: 'common' })}
        />
      )}
    </>
  );
};

export default AddServiceCategoryModal;
